/**
 * `usePoll` — the one polling primitive every poller in the app is built
 * on. Hand-rolled on purpose (no TanStack Query): the surface we need is
 * small enough to own, and owning it is what lets every poller share the
 * same guards.
 *
 * What every poll gets for free:
 *
 * - **Visibility pause.** A hidden tab schedules nothing and aborts the
 *   fetch in flight; becoming visible fires one immediate fetch and then
 *   resumes the cadence. This is what stops N idle tabs from firing a
 *   synchronised burst every five minutes (Chrome aligns background-tab
 *   timers to the same wall-clock boundary).
 * - **In-flight guard.** A tick that lands while a fetch is outstanding
 *   is skipped, never queued — a slow API can't stack requests.
 * - **Abort on supersede.** The `AbortSignal` handed to `queryFn` fires on
 *   unmount, on hide, when a dependency changes and when a manual
 *   `refetch()` overtakes a poll in the air.
 * - **Jitter.** Every delay is spread ±`jitterRatio` so tabs that started
 *   in lockstep drift apart and bursts can't re-form.
 * - **Error backoff.** A failed tick backs off exponentially (never below
 *   the normal cadence, and never below a `Retry-After` the server sent)
 *   and resets on the next success. Errors never throw out of the hook.
 * - **Dynamic cadence, stop conditions, wall-clock cap** — see options.
 * - **ETag no-op ticks.** Return {@link UNCHANGED} from `queryFn` for a 304
 *   and the tick counts as a success without touching `data`.
 * - **Quiet ticks.** A fetch changes nothing its caller renders unless it
 *   brings new data or an error, so a tick that comes back unchanged never
 *   re-renders the page that polls. Whether a fetch is in flight is state
 *   only while there's no data yet, where it decides `isLoading`.
 *
 * A manual `refetch()` is always a real fetch: it bypasses the scheduler,
 * the hidden-tab gate and the ETag (`ctx.etag` is null and `ctx.manual`
 * is true so callers send no `If-None-Match`).
 */

import { DependencyList, useEffect, useMemo, useRef, useState } from 'react';

import { isDocumentVisible, subscribeVisibility } from '@/lib/visibility';

/** Return this from `queryFn` when the server answered 304: the tick is a
 * success (backoff resets, cadence continues) but `data` is left alone. */
export const UNCHANGED: unique symbol = Symbol('usePoll.unchanged');

const TAGGED: unique symbol = Symbol('usePoll.tagged');

interface TaggedResult<T> {
  readonly [TAGGED]: true;
  data: T;
  etag: string | null;
}

/** Wrap a result to hand its ETag back to the poll; the next scheduled
 * tick receives it as `ctx.etag`. */
export function withEtag<T>(data: T, etag: string | null): TaggedResult<T> {
  return { [TAGGED]: true, data, etag };
}

export type PollFetchResult<T> = T | typeof UNCHANGED | TaggedResult<T>;

export interface PollContext {
  /** Fires on unmount, hide, dependency change and manual supersede. */
  signal: AbortSignal;
  /** ETag of the last full payload — null on the first fetch, after a
   * dependency change, and on every manual refetch. */
  etag: string | null;
  /** True for a user-initiated `refetch()`. */
  manual: boolean;
}

export type PollStopReason = 'until' | 'maxDuration' | 'stop';

/** Why a fetch ran: the poll (re)starting, a scheduled tick, the tab
 * becoming visible, or a user-initiated `refetch()`. */
export type PollRunReason = 'start' | 'restart' | 'tick' | 'visible' | 'manual';

export interface PollErrorContext {
  reason: PollRunReason;
  /** Shorthand for `reason === 'manual'`. */
  manual: boolean;
  /** Consecutive failures including this one (drives the backoff). */
  failures: number;
}

export interface PollOptions<T> {
  /** The fetch. Runs at most once at a time. */
  queryFn: (ctx: PollContext) => Promise<PollFetchResult<T>>;
  /**
   * Cadence in ms. A function is re-evaluated against the latest data on
   * every render and after every fetch, so it may also close over local
   * state. `null` means "don't schedule" — `refetch()` still works.
   */
  interval: number | null | ((data: T | undefined) => number | null);
  /** `false` = no fetch, no schedule; the last data is kept. Flipping back
   * to `true` (re)starts the poll. @default true */
  enabled?: boolean;
  /** Fire the first fetch as soon as the poll (re)starts. `false` waits
   * one interval first — for jobs that were literally just kicked off.
   * Applies to the initial start and to every `refetchDeps` restart.
   * @default true */
  immediate?: boolean;
  /** Stop once this holds for the latest data. The data is kept; polling
   * only restarts when `enabled` flips or a `refetchDeps` entry changes. */
  until?: (data: T) => boolean;
  /** Hard wall-clock cap, measured from when the poll (re)started. */
  maxDurationMs?: number;
  /** Any identity change aborts the in-flight fetch, resets the ETag,
   * backoff and wall clock, and restarts (fetching at once unless
   * `immediate` is false). */
  refetchDeps?: DependencyList;
  /** ±ratio applied to every delay. @default 0.2 */
  jitterRatio?: number;
  /** Exponential backoff after failures. @default 1s → 60s, ×2 */
  backoff?: { baseMs?: number; maxMs?: number; factor?: number };
  /** Every successful fetch that carried data (not `UNCHANGED` ticks). */
  onData?: (data: T) => void;
  /** Every failed fetch except aborts we caused ourselves. Do not toast
   * from here — a poll fails quietly and recovers on its own. */
  onError?: (error: unknown, ctx: PollErrorContext) => void;
  /** The poll stopped scheduling (`until`, the wall-clock cap, or
   * `stop()`). */
  onStop?: (reason: PollStopReason) => void;
}

export interface PollController<T> {
  data: T | undefined;
  error: unknown;
  /** First load in flight (or imminent) and no data yet. */
  isLoading: boolean;
  /** Not scheduling because the tab is hidden (or, via `useLeaderPoll`,
   * because another tab is doing the fetching). */
  isPaused: boolean;
  /** Stopped by `until`, the wall-clock cap, or `stop()`. */
  isStopped: boolean;
  /** User-initiated fetch: bypasses the scheduler, the hidden-tab gate
   * and the ETag. Supersedes a poll already in the air. */
  refetch: () => Promise<void>;
  /** Stop scheduling; keep the data; `refetch()` stays live. */
  stop: () => void;
  /** Patch the data locally (optimistic row updates). */
  setData: (updater: T | undefined | ((prev: T | undefined) => T | undefined)) => void;
}

const DEFAULT_JITTER_RATIO = 0.2;
const DEFAULT_BACKOFF = { baseMs: 1_000, maxMs: 60_000, factor: 2 };

/** ±ratio, centred so `Math.random() === 0.5` yields exactly `ms`. */
function jitter(ms: number, ratio: number): number {
  if (ratio <= 0) {
    return ms;
  }
  return Math.round(ms * (1 + ratio * (2 * Math.random() - 1)));
}

/** Errors may carry a server-sent `Retry-After` (see `ApiUnavailableError`). */
function readRetryAfter(error: unknown): number | null {
  const value = (error as { retryAfterMs?: unknown } | null)?.retryAfterMs;
  return typeof value === 'number' && value > 0 ? value : null;
}

function isTagged<T>(result: PollFetchResult<T>): result is TaggedResult<T> {
  return typeof result === 'object' && result !== null && TAGGED in result;
}

/** Bumps whenever any entry of `deps` changes identity. 0 on first render. */
function useDepsVersion(deps: DependencyList | undefined): number {
  const prevRef = useRef(deps);
  const versionRef = useRef(0);
  const prev = prevRef.current;
  if (deps !== prev) {
    const changed =
      !deps ||
      !prev ||
      deps.length !== prev.length ||
      deps.some((entry, i) => !Object.is(entry, prev[i]));
    if (changed) {
      versionRef.current += 1;
    }
  }
  prevRef.current = deps;
  return versionRef.current;
}

export function usePoll<T>(options: PollOptions<T>): PollController<T> {
  const { enabled = true, immediate = true, refetchDeps } = options;

  // The scheduler reads options at fire time, so callers can pass inline
  // functions without restarting the poll on every render.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [data, setDataState] = useState<T | undefined>(undefined);
  const [error, setErrorState] = useState<unknown>(undefined);
  // A fetch in flight while there's no data yet — the only fetch state
  // anything renders (`isLoading`). True from the first render when a fetch
  // is about to fire, so `isLoading` never flashes false before the mount
  // effect runs.
  const [awaitingFirst, setAwaitingFirstState] = useState<boolean>(
    () => enabled && immediate && isDocumentVisible()
  );
  const [isPaused, setIsPaused] = useState<boolean>(() => !isDocumentVisible());
  const [isStopped, setIsStopped] = useState(false);

  const dataRef = useRef<T | undefined>(undefined);
  // Mirrors of the two states every fetch may touch, so a fetch sets them
  // only when they change: a state set to its own value can still render.
  const errorRef = useRef<unknown>(undefined);
  const awaitingFirstRef = useRef(awaitingFirst);
  const etagRef = useRef<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const failuresRef = useRef(0);
  const retryAfterRef = useRef<number | null>(null);
  const startedAtRef = useRef<number | null>(null);
  const stoppedRef = useRef(false);
  const visibleRef = useRef(isDocumentVisible());
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  // Bumped on every (re)start so completions from an earlier run are
  // discarded instead of writing stale data.
  const generationRef = useRef(0);
  const mountedRef = useRef(false);
  const lastIntervalRef = useRef<number | null | undefined>(undefined);

  const machine = useMemo(() => {
    const setError = (next: unknown) => {
      if (!Object.is(errorRef.current, next)) {
        errorRef.current = next;
        setErrorState(next);
      }
    };

    /** In flight: state only while there's no data to show yet. */
    const setFetching = (fetching: boolean) => {
      const next = fetching && dataRef.current === undefined;
      if (awaitingFirstRef.current !== next) {
        awaitingFirstRef.current = next;
        setAwaitingFirstState(next);
      }
    };

    const clearTimer = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    const abortInFlight = () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
        setFetching(false);
      }
    };

    const canSchedule = () =>
      mountedRef.current && enabledRef.current && visibleRef.current && !stoppedRef.current;

    const evalInterval = (): number | null => {
      const { interval } = optionsRef.current;
      return typeof interval === 'function' ? interval(dataRef.current) : interval;
    };

    const stop = (reason: PollStopReason) => {
      if (stoppedRef.current) {
        return;
      }
      stoppedRef.current = true;
      clearTimer();
      setIsStopped(true);
      optionsRef.current.onStop?.(reason);
    };

    const scheduleNext = () => {
      clearTimer();
      if (!canSchedule()) {
        return;
      }
      const base = evalInterval();
      lastIntervalRef.current = base;
      if (base === null) {
        return;
      }
      let delay = base;
      if (failuresRef.current > 0) {
        const { baseMs, maxMs, factor } = { ...DEFAULT_BACKOFF, ...optionsRef.current.backoff };
        const backoff = Math.min(maxMs, baseMs * factor ** (failuresRef.current - 1));
        // Backing off never means polling FASTER than the normal cadence,
        // and never sooner than the server asked us to wait.
        delay = Math.max(delay, backoff, retryAfterRef.current ?? 0);
      }
      const ratio = optionsRef.current.jitterRatio ?? DEFAULT_JITTER_RATIO;
      timerRef.current = setTimeout(
        () => {
          timerRef.current = null;
          void run('tick');
        },
        jitter(delay, ratio)
      );
    };

    const pastWallClock = () => {
      const cap = optionsRef.current.maxDurationMs;
      return (
        cap !== undefined &&
        startedAtRef.current !== null &&
        Date.now() - startedAtRef.current >= cap
      );
    };

    async function run(reason: PollRunReason): Promise<void> {
      const manual = reason === 'manual';
      if (!mountedRef.current) {
        return;
      }
      if (!manual && !visibleRef.current) {
        return;
      }
      if (controllerRef.current) {
        if (!manual) {
          return; // in-flight guard: a due tick is skipped, not queued
        }
        controllerRef.current.abort(); // a manual refetch overtakes the poll in the air
      }
      const controller = new AbortController();
      controllerRef.current = controller;
      const generation = generationRef.current;
      setFetching(true);

      const stale = () =>
        controllerRef.current !== controller ||
        generation !== generationRef.current ||
        !mountedRef.current;

      try {
        const result = await optionsRef.current.queryFn({
          signal: controller.signal,
          etag: manual ? null : etagRef.current,
          manual,
        });
        if (stale()) {
          return;
        }
        failuresRef.current = 0;
        retryAfterRef.current = null;
        setError(undefined);
        if (result !== UNCHANGED) {
          const next = isTagged(result) ? result.data : result;
          etagRef.current = isTagged(result) ? result.etag : null;
          dataRef.current = next;
          setDataState(() => next);
          optionsRef.current.onData?.(next);
          if (optionsRef.current.until?.(next)) {
            stop('until');
          }
        }
      } catch (err) {
        if (stale()) {
          return; // an abort we caused, or a run that was superseded
        }
        failuresRef.current += 1;
        retryAfterRef.current = readRetryAfter(err);
        setError(err);
        optionsRef.current.onError?.(err, { reason, manual, failures: failuresRef.current });
      } finally {
        if (controllerRef.current === controller) {
          controllerRef.current = null;
          setFetching(false);
          if (generation === generationRef.current && mountedRef.current) {
            if (pastWallClock()) {
              stop('maxDuration');
            }
            scheduleNext();
          }
        }
      }
    }

    /** (Re)start: fresh generation, clock, ETag and backoff. `restart`
     * marks a dependency change while already enabled. */
    const start = (reason: 'start' | 'restart') => {
      generationRef.current += 1;
      clearTimer();
      abortInFlight();
      stoppedRef.current = false;
      setIsStopped(false);
      failuresRef.current = 0;
      retryAfterRef.current = null;
      etagRef.current = null;
      startedAtRef.current = Date.now();
      if (!visibleRef.current) {
        return; // resume() fetches when the tab comes back
      }
      if (optionsRef.current.immediate ?? true) {
        void run(reason);
      } else {
        scheduleNext();
      }
    };

    const disable = () => {
      generationRef.current += 1;
      clearTimer();
      abortInFlight();
    };

    const pause = () => {
      visibleRef.current = false;
      setIsPaused(true);
      clearTimer();
      abortInFlight();
    };

    const resume = () => {
      visibleRef.current = true;
      setIsPaused(false);
      if (canSchedule()) {
        void run('visible');
      }
    };

    /** After a render: re-arm when the computed cadence changed (a
     * function interval closing over local state, or a new number). */
    const rearm = () => {
      if (!canSchedule() || controllerRef.current) {
        return; // a fetch in flight reschedules when it lands
      }
      if (evalInterval() !== lastIntervalRef.current) {
        scheduleNext();
      }
    };

    const teardown = () => {
      generationRef.current += 1;
      clearTimer();
      if (controllerRef.current) {
        controllerRef.current.abort();
        controllerRef.current = null;
      }
    };

    const setData: PollController<T>['setData'] = (updater) => {
      const next =
        typeof updater === 'function'
          ? (updater as (prev: T | undefined) => T | undefined)(dataRef.current)
          : updater;
      dataRef.current = next;
      setDataState(() => next);
    };

    return {
      run,
      start,
      disable,
      pause,
      resume,
      rearm,
      teardown,
      stop: () => stop('stop'),
      setData,
    };
  }, []);

  // Mount / unmount. Declared first so it runs before the start effect.
  useEffect(() => {
    mountedRef.current = true;
    const unsubscribe = subscribeVisibility((visible) => {
      if (visible) {
        machine.resume();
      } else {
        machine.pause();
      }
    });
    return () => {
      mountedRef.current = false;
      unsubscribe();
      machine.teardown();
    };
  }, [machine]);

  // One effect for both triggers: enabling and a dependency change that
  // land in the same render start the poll once, not twice.
  const depsVersion = useDepsVersion(refetchDeps);
  const lastStartRef = useRef<{ enabled: boolean; depsVersion: number } | null>(null);
  useEffect(() => {
    const prev = lastStartRef.current;
    lastStartRef.current = { enabled, depsVersion };
    if (!enabled) {
      machine.disable();
      return;
    }
    machine.start(prev?.enabled && prev.depsVersion !== depsVersion ? 'restart' : 'start');
  }, [enabled, depsVersion, machine]);

  // No dependency list on purpose: the cadence may derive from state the
  // caller closes over, so re-check it after every render.
  useEffect(() => {
    machine.rearm();
  });

  const refetch = useMemo(() => () => machine.run('manual'), [machine]);

  return {
    data,
    error,
    // "No data yet and some is coming": covers the fetch in flight, the
    // hidden tab that will fetch on becoming visible, and the deferred
    // first tick of an `immediate: false` poll.
    isLoading:
      data === undefined && (awaitingFirst || (enabled && !isStopped && error === undefined)),
    isPaused,
    isStopped,
    refetch,
    stop: machine.stop,
    setData: machine.setData,
  };
}
